<template>
  <DeepDialog
    v-model="dialog"
    fullscreen-on-mobile
    :title="i18n.t('titles.sign_error_dialog')"
    width="600"
    @keydown.esc="closeDialog"
  >
    <template #card-title-content-icon>
      <div v-if="doesKeyExists" class="d-flex justify-center align-center mr-4">
        <div v-if="isSmsCode">
          {{ i18n.t('authorities.sms_code') }}
        </div>
        <img
          v-else-if="currentDocument.signatureMode !== SignatureMode.TIMESTAMP"
          alt=""
          class="authority-service-logo"
          :src="`/svg/authority-services/${authorityService}-logo.svg`"
        />
      </div>
    </template>
    <template #content>
      <v-card
        class="error-card pa-6"
        color="grey-lighten-4"
        :flat="true"
        rounded="lg"
      >
        <v-alert class="error-alert" color="error" rounded variant="outlined">
          <div class="d-flex flex-row align-center">
            <v-icon class="mr-2" color="error" size="14">
              fal fa-circle-exclamation
            </v-icon>

            <div class="alert-text">
              {{ i18n.t('texts.signing_not_possible') }}
              <a
                v-if="
                  authorityService === AuthorityService.MOBILE_ID &&
                  errorKey === 'error.sign.user.serialNumber.mismatch'
                "
                :href="i18n.t('links.support_page')"
              >
                {{ i18n.t('texts.mobile_id.serialNumber.mismatch') }}
              </a>
              <a
                v-else-if="
                  !isConcurrentModification &&
                  doesKeyExists &&
                  isIdentityRelated
                "
                class="text-primary"
                :href="learnMoreLink"
                target="_blank"
              >
                {{ i18n.t('texts.learn_more') }}</a
              >
            </div>
          </div>
        </v-alert>
        <i18n-t
          v-if="documentError"
          class="error-description"
          :keypath="`errors.api${authorityTranslationKey}.${documentError}`"
          scope="global"
          tag="p"
        >
          <template #email>
            <a
              v-if="authorityService === 'did'"
              class="text-primary"
              href="mailto:support@deepid.swiss"
              >support@deepid.swiss</a
            >
            <a v-else class="text-primary" href="mailto:support@deepcloud.swiss"
              >support@deepcloud.swiss</a
            >
          </template>
        </i18n-t>
        <i18n-t
          v-else
          class="error-description"
          :keypath="getErrorPath()"
          scope="global"
          tag="p"
        >
          <template #email>
            <a class="text-primary" href="mailto:support@deepcloud.swiss"
              >support@deepcloud.swiss</a
            >
          </template>
        </i18n-t>
        <div v-if="!doesKeyExists" class="error-description">
          {{ i18n.t('texts.sign_error_dialog_reference') }}:
          <p v-if="errorKey">
            {{ errorKey }}
          </p>
          <p v-if="documentError">
            {{ documentError }}
          </p>
        </div>
      </v-card>
    </template>
    <template #actions>
      <component
        :is="shouldBePrimary"
        :text="buttonText"
        @click="closeDialog"
      />
      <VBtnPrimary
        v-if="
          authorityService === AuthorityService.MOBILE_ID &&
          errorKey &&
          errorKey.includes('mismatch')
        "
        :text="i18n.t('texts.mobile_id.serialNumber.mismatch')"
        @click="handleMismatch"
      >
      </VBtnPrimary>
    </template>
  </DeepDialog>
</template>

<script setup lang="ts">
import { constants } from '@/constants/constants';
import { AuthorityService } from '@/types/enums/AuthorityService.ts';
import { computed, onUnmounted, ref } from 'vue';
import { useEmitter } from '@/composables/useEmitter.ts';
import { useI18n } from 'vue-i18n';
import { DeepDialog } from '@deepcloud/deep-ui-lib';
import { useDocumentContext } from '@/composables/useDocumentContext';
import { SignatureMode } from '@/types/enums/SignatureMode';
import { getAutographErrorPath } from '@/utils/autographErrors';

const { currentDocument } = useDocumentContext();
const emitter = useEmitter();
const i18n = useI18n();
const dialog = ref(false);
const errorKey = ref();
const documentError = ref(null);
const authorityService = ref(null);

const UNKNOWN_ERROR = 'unknown_error';

emitter.$on('show-sign-error-dialog', showDialog);

onUnmounted(() => {
  emitter.$off('show-sign-error-dialog', showDialog);
});

const isSmsCode = computed(() => {
  return (
    currentDocument.value?.signatureMode === SignatureMode.ADVANCED &&
    authorityService.value === AuthorityService.MOBILE_ID
  );
});

const authorityTranslationKey = computed(() => {
  switch (authorityService.value) {
    case AuthorityService.DEEP_ID:
      return '.deep_id';
    case AuthorityService.MOBILE_ID:
      return '.mobile_id';
    default:
      return '';
  }
});

const learnMoreLink = computed(() => {
  let linkKey = '#';
  if (authorityService.value === AuthorityService.DEEP_ID) {
    linkKey = 'links.qes_deepid_failed';
  }
  if (authorityService.value === AuthorityService.MOBILE_ID) {
    linkKey = 'links.qes_mobileid_failed';
  }
  return i18n.t(linkKey);
});

const shouldBePrimary = computed(() => {
  return authorityService.value === AuthorityService.MOBILE_ID &&
    errorKey.value &&
    errorKey.value.includes('mismatch')
    ? 'VBtnSecondary'
    : 'VBtnPrimary';
});

const isConcurrentModification = computed(() => {
  return errorKey.value === constants.SIGN_ERROR_CONCURRENT_MODIFICATION;
});

const autographErrorPath = computed(() => {
  return getAutographErrorPath(errorKey.value);
});

const doesKeyExists = computed(() => {
  if (autographErrorPath.value) {
    return true;
  }
  return (
    i18n.te(`errors.api${authorityTranslationKey.value}.${errorKey.value}`) ||
    (i18n.te(
      `errors.api${authorityTranslationKey.value}.${documentError.value}`
    ) &&
      documentError.value !== UNKNOWN_ERROR) ||
    (documentError.value !== UNKNOWN_ERROR && i18n.te(errorKey.value)) ||
    i18n.te(`errors.api.${errorKey.value}`)
  );
});

const isIdentityRelated = computed(() => {
  return (
    i18n.te(`errors.api${authorityTranslationKey.value}.${errorKey.value}`) ||
    i18n.te(`errors.api${authorityTranslationKey.value}.${documentError.value}`)
  );
});

const buttonText = computed(() => {
  return i18n.t(
    isConcurrentModification.value
      ? 'buttons.close_and_refresh'
      : 'buttons.close'
  );
});

function handleMismatch() {
  const url =
    i18n.locale.value === 'de'
      ? 'https://support.deepbox.swiss/videos/deep-technologien/deepsign/signieren/qes-mit-mobile-id-nicht-moeglich/'
      : `https://support.deepbox.swiss/${i18n.locale.value}/videos/deep-technologies/deepsign/signieren/qes-mit-mobile-id-nicht-moeglich/`;
  window.open(url, '_blank');
}

//Our API error keys are not standardized, so we need to check multiple paths to find the correct translation key
function getErrorPath() {
  if (!doesKeyExists.value) {
    return 'errors.api.error.unknown_error';
  }
  //Autograph errors are keyed differently from the rest of the API errors
  if (autographErrorPath.value) {
    return autographErrorPath.value;
  }

  if (i18n.te(`errors.api.${errorKey.value}`)) {
    return `errors.api.${errorKey.value}`;
  }

  if (i18n.te(`errors.api${authorityTranslationKey.value}.${errorKey.value}`)) {
    return `errors.api${authorityTranslationKey.value}.${errorKey.value}`;
  }

  if (
    i18n.te(`errors.api${authorityTranslationKey.value}.${documentError.value}`)
  ) {
    return `errors.api${authorityTranslationKey.value}.${documentError.value}`;
  }
  return 'errors.api.error.unknown_error';
}

function showDialog(event) {
  errorKey.value = event.errorKey;
  authorityService.value = event.authorityService;
  documentError.value = getDocumentError(event.documentErrors);
  if (!documentError.value && !errorKey.value) {
    documentError.value = UNKNOWN_ERROR;
  }
  dialog.value = true;
}

function getDocumentError(errors) {
  let orderedError = null;
  let orderNumber = null;
  if (errors?.documentIssues) {
    for (const issue of errors.documentIssues) {
      if (
        orderNumber > constants.SIGN_ERRORS_KEY_ORDER.indexOf(issue) ||
        orderNumber === null
      ) {
        orderNumber = constants.SIGN_ERRORS_KEY_ORDER.indexOf(issue);
        orderedError = issue;
      }
    }
    return orderedError;
  }
  return null;
}

function closeDialog() {
  if (isConcurrentModification.value) {
    emitter.$emit('update-sign-status');
  }
  dialog.value = false;
}
</script>

<style lang="scss" scoped>
.authority-service-logo {
  height: 20px;
}

.alert-text {
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: rgb(var(--v-theme-error));

  :deep(a) {
    text-decoration: none !important;
  }
}

.error-description {
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: black;
  padding-top: 1rem;
}

.error-alert {
  background-color: white !important;
  padding: 12px;
}

.logo {
  display: flex;
  align-items: center;
  justify-content: center;
}

.logo img {
  width: 30%;
}
</style>
